/**
 * Exact decimal money arithmetic for the live edit preview (PGSS-5025 D1).
 *
 * WHY THIS EXISTS
 * ---------------
 * The order/quote editor must show unit price + totals live, before anything is saved. That preview has
 * to equal what the backend will actually store — otherwise the user is shown one number and the system
 * keeps another, and the two disagree by a cent across views.
 *
 * The backend computes money with Java BigDecimal (exact decimal, HALF_UP). JavaScript `Number` is an
 * IEEE-754 binary double and CANNOT represent decimal cents exactly — 72.62 is really
 * 72.620000000000005684 — so `float maths + .toFixed(2)` rounds DOWN exactly where BigDecimal HALF_UP
 * rounds UP. That is a hard 1-cent split, and no amount of sprinkling `.toFixed()` through the call
 * chain can fix it: this same bug has been "fixed" three times (2025-01-31, 2026-04-20, 2026-04-26),
 * each time in a different function, and each time it reappeared in the next one that wasn't patched.
 *
 * So: do the arithmetic on scaled BigInts (exact decimal), and round ONLY where the backend rounds.
 * Parity with the backend is pinned by money.parity.test.js — do not "simplify" these back to floats.
 */

const SCALE = 12n; // internal precision, well beyond any money input
const POW = 10n ** SCALE;
const HUNDRED = 100n * POW;

/**
 * Parse a decimal value into a BigInt scaled by 10^SCALE — exactly, and NEVER throwing.
 *
 * We go via String(): the double 0.35 is not exactly 0.35 in binary, but String(0.35) === "0.35" — the
 * shortest representation that round-trips, i.e. the decimal the user/DB actually meant. Parsing that
 * string sidesteps the binary error entirely.
 *
 * MUST NOT THROW. `BigInt("1e-7")` and `BigInt(2.5)` both throw, and a crash in the order editor is far
 * worse than a rounding error — so exponential notation is expanded here, and anything unparseable
 * (NaN, Infinity, "abc", null) degrades to 0 exactly as the old float code effectively did.
 */
function toBig(value) {
  if (value === null || value === undefined || value === '') {
    return 0n;
  }
  const s = String(value).trim();
  // sign, integer part, fraction, optional exponent (String(1e-7) === "1e-7", String(1e21) === "1e+21")
  const m = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(s);
  if (!m) {
    return 0n; // "abc", "NaN", "Infinity", "1.2.3" …
  }
  const sign = m[1] === '-' ? -1n : 1n;
  const intPart = m[2] || '';
  const fracPart = m[3] || '';
  // Bound the magnitude AFTER Number() — do not reject on raw digit length, or padded
  // valid forms like "1e-0001" / "1e00001" wrongly become 0. Pathological "1e-"+"9".repeat(400)
  // still becomes Infinity and is rejected by isFinite / MAX_EXP.
  const MAX_EXP = 100;
  const exp = Number(m[4] || 0);
  if (!Number.isFinite(exp) || Math.abs(exp) > MAX_EXP) {
    return 0n;
  }

  const digits = intPart + fracPart;
  if (digits === '') {
    return 0n;
  }
  // value = digits x 10^(pointPos - digits.length); we want it scaled by 10^SCALE
  const pointPos = intPart.length + exp;
  const shift = Number(SCALE) + pointPos - digits.length;
  if (!Number.isFinite(shift) || Math.abs(shift) > MAX_EXP + Number(SCALE)) {
    return 0n;
  }

  let big = BigInt(digits);
  if (shift >= 0) {
    big *= 10n ** BigInt(shift);
  } else {
    big /= 10n ** BigInt(-shift); // beyond our 12dp of headroom — money never needs it
  }
  return sign * big;
}

/** Divide two scaled BigInts, rounding HALF_UP to `dp` decimal places. Returns a scaled BigInt. */
function divideHalfUp(numerator, denominator, dp = 2) {
  if (denominator === 0n) {
    return 0n;
  }
  const dpPow = 10n ** BigInt(dp);
  // value = numerator/denominator, expressed in units of 1/dpPow, rounded HALF_UP
  const scaledNum = numerator * dpPow;
  const neg = (scaledNum < 0n) !== (denominator < 0n);
  const a = scaledNum < 0n ? -scaledNum : scaledNum;
  const b = denominator < 0n ? -denominator : denominator;
  let q = a / b;
  const rem = a % b;
  if (rem * 2n >= b) {
    q += 1n; // HALF_UP (away from zero) — matches BigDecimal.ROUND_HALF_UP
  }
  if (neg) q = -q;
  return (q * POW) / dpPow;
}

/** Round a scaled BigInt HALF_UP to `dp` places, returning a scaled BigInt. */
function roundHalfUp(scaled, dp = 2) {
  return divideHalfUp(scaled, POW, dp);
}

/** Render a scaled BigInt as a fixed-2dp string, e.g. "132.63". */
function toMoneyString(scaled) {
  const rounded = roundHalfUp(scaled, 2);
  const neg = rounded < 0n;
  const abs = neg ? -rounded : rounded;
  const cents = abs / (POW / 100n);
  const whole = cents / 100n;
  const frac = cents % 100n;
  return `${neg ? '-' : ''}${whole}.${frac.toString().padStart(2, '0')}`;
}

/**
 * unit_price = ( fob + weight*shipping + fob*tariff% ) / ( 1 - margin% )
 *
 * Mirrors com.clovation.platform.psg.api.util.PriceCalculationUtil.calculateUnitPrice EXACTLY:
 * the numerator/denominator are built without any intermediate rounding, and HALF_UP to 2dp is applied
 * only on the final divide. tariff/margin are whole percentages (10 = 10%).
 */
export function calculateUnitPriceExact(weight, fob, tariff, shipping, margin = 0) {
  const fobB = toBig(fob);
  const weightB = toBig(weight);
  const shippingB = toBig(shipping);
  const tariffB = toBig(tariff);
  const marginB = toBig(margin);

  // fractions (scaled): pct/100
  const tariffFrac = (tariffB * POW) / HUNDRED;
  const marginFrac = (marginB * POW) / HUNDRED;

  const denominator = POW - marginFrac; // 1 - margin%
  if (denominator <= 0n) {
    return '0.00'; // margin >= 100% — backend throws; the editor must not crash
  }

  const numerator = fobB + (weightB * shippingB) / POW + (fobB * tariffFrac) / POW;

  return toMoneyString(divideHalfUp(numerator, denominator, 2));
}

/**
 * Line total = gross - round2(gross * discount%), where gross = unitPrice * qty.
 *
 * Mirrors OrderCalculationService.calculateLineTotal -> applyDiscount -> calculateDiscountAmount EXACTLY:
 * the backend does NOT round the gross, it rounds only the discount. (The old FE rounded the gross first —
 * that was a float workaround, unnecessary once the arithmetic is exact, and it made the FE diverge from the
 * BE whenever a unit price carried more than 2dp.)
 *
 * quantity goes through toBig, not BigInt(...): BigInt(2.5) THROWS, and a crashed editor is far worse than
 * a rounding error.
 */
export function calculateLineTotalExact(unitPrice, quantity, discountPct = 0) {
  const gross = (toBig(unitPrice) * toBig(quantity)) / POW;
  const discount = roundHalfUp((gross * toBig(discountPct)) / HUNDRED, 2);
  return toMoneyString(gross - discount);
}

/** Surcharge = round2(productTotal * pct%) — mirrors OrderCalculationService.calculateSurchargeAmount. */
export function calculateSurchargeExact(productTotal, surchargePct = 0) {
  return toMoneyString(roundHalfUp((toBig(productTotal) * toBig(surchargePct)) / HUNDRED, 2));
}

/**
 * Discount amount for a whole line = round2(unitPrice * qty * pct%).
 * Mirrors OrderCalculationService.calculateDiscountAmount (which does not round the gross first).
 *
 * Same FORMULA as the old FE version — the old one just accumulated float error. (It is NOT a different
 * operation order: multiplication commutes.) NOTE the caller in AuraFunctions truncates the quantity to an
 * integer to preserve the legacy `parseInt(quantity, 10)` contract; this module itself stays general.
 */
export function getDiscountAmtExact(discountPct, unitPrice, quantity) {
  const gross = (toBig(unitPrice) * toBig(quantity)) / POW;
  return toMoneyString(roundHalfUp((gross * toBig(discountPct)) / HUNDRED, 2));
}

/**
 * Single unit price with its discount applied = round2( unitPrice * (1 - pct%) ) — rounded ONCE.
 *
 * NOTE: this is deliberately NOT the backend's applyDiscount. The backend applies a discount to the LINE
 * gross (unitPrice x quantity) and has no discounted-unit-price concept at all — this value is display-only,
 * with no stored counterpart. So we keep the original semantics (round once at the end) and only make the
 * arithmetic exact. Mirroring the line-level rounding here would silently change a displayed number
 * (59.50 @ 5%: 56.53 -> 56.52) with nothing in the backend to justify it.
 */
export function calculateUnitAmountExact(unitPrice, discountPct = 0) {
  const price = toBig(unitPrice);
  const discounted = price - (price * toBig(discountPct)) / HUNDRED; // exact, no intermediate rounding
  return toMoneyString(discounted);
}

/**
 * margin% = 100 * (unitPrice - (fob + weight*shipping + fob*tariff%)) / unitPrice
 *
 * The inverse of {@link calculateUnitPriceExact} — used when the user types a unit price directly and the
 * editor back-solves the margin. Same exact-decimal treatment so it round-trips with the unit price.
 */
export function calculateMarginExact(weight, fob, tariff, shipping, unitPrice) {
  const priceB = toBig(unitPrice);
  if (priceB === 0n) {
    return '0.00';
  }
  const cost = toBig(fob)
    + (toBig(weight) * toBig(shipping)) / POW
    + (toBig(fob) * ((toBig(tariff) * POW) / HUNDRED)) / POW;
  const numerator = (priceB - cost) * 100n;
  return toMoneyString(divideHalfUp(numerator, priceB, 2));
}

/**
 * Exact sum of money values, rounded ONCE at the end — matching the original
 * `(total - discountAmt) + Number(textPrice) + Number(imagePrice)` then `.toFixed(2)`.
 * (Do not round each addend: that would change the displayed total for non-2dp VAS prices.)
 */
export function addMoneyExact(...values) {
  const sum = values.reduce((acc, v) => acc + toBig(v), 0n);
  return toMoneyString(sum);
}

/**
 * VAS price-list line total = base + base*margin% - base*discount%, where base = unitPrice * qty,
 * rounded ONCE at the end (VasPriceList.handleChange's original semantics — a distinct formula from the
 * order line total; do not "unify" them). Display/entry-side only; exactness just removes the float error.
 */
export function calculateVasLineTotalExact(unitPrice, quantity, marginPct = 0, discountPct = 0) {
  const base = (toBig(unitPrice) * toBig(quantity)) / POW;
  const margin = (base * toBig(marginPct)) / HUNDRED;
  const discount = (base * toBig(discountPct)) / HUNDRED;
  return toMoneyString(base + margin - discount);
}

export const __internals = { toBig, toMoneyString, divideHalfUp, roundHalfUp };
